import { Link } from "wouter";
import {
  TrendingUp,
  MapPin,
  Briefcase,
  Building2,
  DollarSign,
  BarChart3,
  Clock,
  Navigation,
  Zap,
  School,
  Hospital,
  ShoppingCart,
} from "lucide-react";

const InvestmentLocation = () => {
  const investmentHighlights = [
    {
      icon: TrendingUp,
      title: "10.91% Annual Appreciation",
      description: "Property rates increased significantly within the last year",
    },
    {
      icon: BarChart3,
      title: "7-9% Rental Yield",
      description: "Above OMR average of 5-6%, strong income potential",
    },
    {
      icon: DollarSign,
      title: "Starting from ₹53.41 Lakh",
      description: "Affordable luxury with flexible payment plans",
    },
    {
      icon: Zap,
      title: "Fast-Growing Hub",
      description: "Kelambakkam identified as high-growth residential area",
    },
    {
      icon: Building2,
      title: "Resale Value Growth",
      description: "Expected appreciation due to ongoing area development",
    },
    {
      icon: Clock,
      title: "Ready to Move",
      description: "RERA-approved with possession-ready status",
    },
  ];

  const connectivity = [
    {
      icon: Briefcase,
      title: "Siruseri IT Park",
      distance: "5 km",
      description: "Major IT employment hub",
    },
    {
      icon: Navigation,
      title: "Chennai Airport",
      distance: "20 km",
      description: "International connectivity",
    },
    {
      icon: Zap,
      title: "Metro Station",
      distance: "12 km",
      description: "Sholinganallur Junction",
    },
    {
      icon: Building2,
      title: "Railway Station",
      distance: "8 km",
      description: "Kelambakkam railway station",
    },
    {
      icon: ShoppingCart,
      title: "Shopping Centers",
      distance: "1 km",
      description: "Supermarkets and retail",
    },
    {
      icon: School,
      title: "Schools & Colleges",
      distance: "2-5 km",
      description: "Educational institutions",
    },
    {
      icon: Hospital,
      title: "Hospitals",
      distance: "2-3 km",
      description: "Healthcare facilities",
    },
    {
      icon: Building2,
      title: "Banks & ATMs",
      distance: "1-2 km",
      description: "Financial services",
    },
  ];

  const futureGrowth = [
    {
      title: "Chennai Metro Expansion",
      description: "Planned renovation and extension of metro network",
      impact: "Enhanced public transportation connectivity",
    },
    {
      title: "Six-Lane OMR Extension",
      description: "Highway expansion project in progress",
      impact: "Improved road connectivity and reduced travel time",
    },
    {
      title: "New Bus Terminal",
      description: "Recently added transportation hub",
      impact: "Better public transport accessibility",
    },
    {
      title: "Siruseri IT Park Growth",
      description: "Expansion of major IT employment center",
      impact: "Increased job opportunities and demand",
    },
    {
      title: "GST Road Connectivity",
      description: "Enhanced accessibility to major commercial zones",
      impact: "Better connectivity to business districts",
    },
    {
      title: "Residential Area Development",
      description: "Ongoing infrastructure improvements",
      impact: "Rising property values and demand",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="section-container py-4 flex items-center justify-between">
          <Link href="/">
            <a className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2">
              ← Back to Home
            </a>
          </Link>
          <h1 className="text-xl font-bold text-foreground">Investment & Location</h1>
          <div className="w-32"></div>
        </div>
      </div>

      <main className="py-12 sm:py-16">
        <div className="section-container">
          <div className="max-w-4xl">
            {/* Investment Highlights */}
            <section className="mb-16">
              <h2 className="section-title mb-4">Investment Highlights</h2>
              <p className="section-subtitle mb-12">
                Strong returns and appreciation potential in a growing market
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {investmentHighlights.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <Icon className="w-6 h-6 text-blue-600 mt-1" />
                        </div>
                        <div>
                          <h3 className="font-bold text-foreground mb-2">{item.title}</h3>
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Location & Connectivity */}
            <section className="mb-16">
              <h2 className="section-title mb-4">Strategic Location & Connectivity</h2>
              <p className="section-subtitle mb-12">
                Well-connected to major employment hubs, airports, and essential services
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {connectivity.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-lg p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-300"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <Icon className="w-6 h-6 text-blue-600" />
                        <span className="text-sm font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                          {item.distance}
                        </span>
                      </div>
                      <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Future Growth Drivers */}
            <section className="mb-16">
              <h2 className="section-title mb-4">Future Growth Drivers</h2>
              <p className="section-subtitle mb-12">
                Ongoing infrastructure development supporting long-term appreciation
              </p>

              <div className="space-y-4">
                {futureGrowth.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-blue-600 font-bold">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-foreground mb-2">{item.title}</h3>
                        <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                        <div className="flex items-center gap-2 text-sm text-blue-600 font-semibold">
                          <Zap className="w-4 h-4" />
                          {item.impact}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Key Statistics */}
            <section className="bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-200 rounded-lg p-8">
              <h3 className="text-xl font-bold text-foreground mb-8">Location & Investment Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-3xl font-bold text-blue-600">10.5</p>
                  <p className="text-sm text-muted-foreground mt-2">Acres Land Area</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">729</p>
                  <p className="text-sm text-muted-foreground mt-2">Total Units</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">10.91%</p>
                  <p className="text-sm text-muted-foreground mt-2">Annual Appreciation</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">7-9%</p>
                  <p className="text-sm text-muted-foreground mt-2">Rental Yield</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InvestmentLocation;
